import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Plus, Trash2, Pencil, Briefcase, Clock, ChevronRight, Loader2, Save, X, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const blank = () => ({ career_start_date: '', breaks: [], previous_orgs: [] });

export const ProvenanceTab = ({ employeeId, canEdit = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(blank());
  const [zoomItem, setZoomItem] = useState(null);

  useEffect(() => {
    if (!employeeId) return;
    const fetchProv = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/employees/${employeeId}/provenance`, { withCredentials: true });
        setData(res.data);
        setForm({
          career_start_date: res.data.career_start_date || '',
          breaks: (res.data.breaks || []).map((b) => ({ ...b })),
          previous_orgs: (res.data.previous_orgs || []).map((o) => ({ ...o })),
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load provenance');
      } finally {
        setLoading(false);
      }
    };
    fetchProv();
  }, [employeeId]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/employees/${employeeId}/provenance`, form, { withCredentials: true });
      setData(res.data);
      setIsEditing(false);
      toast.success('Provenance updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading provenance…</div>;
  }
  if (!data) return null;

  // Sorted timeline events
  const events = [];
  (data.previous_orgs || []).forEach((o, i) => events.push({ key: `o-${i}`, kind: 'org', ...o }));
  (data.breaks || []).forEach((b, i) => events.push({ key: `b-${i}`, kind: 'break', name: b.reason || 'Career break', start_date: b.from, end_date: b.to }));
  if (data.current_org_joining_date) {
    events.push({ key: 'current', kind: 'current', name: data.current_org_name || 'Current Organisation', start_date: data.current_org_joining_date, end_date: null });
  }
  events.sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Briefcase className="w-4 h-4 text-primary" /> Career Provenance
          </CardTitle>
          <CardDescription>Career history with auto-calculated experience (breaks excluded).</CardDescription>
        </div>
        {canEdit && !isEditing && (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setIsEditing(true)} data-testid="prov-edit-btn">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {/* Headline experience strip */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div className="rounded-lg border bg-gradient-to-br from-emerald-50 to-sky-50 dark:from-emerald-950/30 dark:to-sky-950/20 p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Experience</p>
            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 mt-1" data-testid="prov-experience">{data.experience.label}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{data.experience.effective_months} effective months</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Career Started</p>
            <p className="text-lg font-semibold mt-1">{data.career_start_date ? new Date(data.career_start_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{(data.previous_orgs || []).length} previous org(s)</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Career Breaks</p>
            <p className="text-lg font-semibold mt-1">{(data.breaks || []).length} break{(data.breaks || []).length === 1 ? '' : 's'}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{data.experience.break_months} months excluded</p>
          </div>
        </div>

        {!isEditing ? (
          /* ---------- VIEW MODE: Timeline ---------- */
          <>
            {events.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-6">
                {canEdit ? 'No history recorded yet — click "Edit" to add the first entry.' : 'No history recorded yet.'}
              </p>
            ) : (
              <div className="relative pl-6">
                <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-gradient-to-b from-primary/30 via-primary/50 to-transparent" />
                {events.map((ev) => (
                  <div key={ev.key} className="relative pb-4 group" data-testid={`prov-event-${ev.key}`}>
                    <span className={`absolute -left-5 top-1 w-3 h-3 rounded-full ring-2 ring-background ${
                      ev.kind === 'current' ? 'bg-emerald-500' : ev.kind === 'break' ? 'bg-amber-500' : 'bg-primary'
                    }`} />
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-sm">{ev.name}</p>
                          {ev.kind === 'current' && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300">Current</Badge>}
                          {ev.kind === 'break' && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300">Break</Badge>}
                          {ev.role && <span className="text-xs text-muted-foreground">· {ev.role}</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {ev.start_date ? new Date(ev.start_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}
                          {' → '}
                          {ev.end_date ? new Date(ev.end_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'Present'}
                        </p>
                      </div>
                      <button
                        onClick={() => setZoomItem(ev)}
                        className="p-1.5 rounded-md hover:bg-muted opacity-0 group-hover:opacity-100 transition shrink-0"
                        data-testid={`prov-zoom-${ev.key}`}
                        title="View visual detail"
                      >
                        <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* ---------- EDIT MODE ---------- */
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Career Start Date</Label>
                <Input type="date" value={form.career_start_date} onChange={(e) => setForm({ ...form, career_start_date: e.target.value })} data-testid="prov-career-start" />
              </div>
              <div className="flex items-end text-xs text-muted-foreground">
                Joining date at Wisewit: <span className="font-medium ml-1 text-foreground">{data.current_org_joining_date || '—'}</span>
              </div>
            </div>

            {/* Previous Orgs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Previous Organisations</Label>
                <Button size="sm" variant="outline" className="gap-1 h-7 text-xs"
                  onClick={() => setForm({ ...form, previous_orgs: [...form.previous_orgs, { name: '', role: '', start_date: '', end_date: '' }] })}
                  data-testid="prov-add-org"
                >
                  <Plus className="w-3 h-3" /> Add Org
                </Button>
              </div>
              <div className="space-y-2">
                {form.previous_orgs.length === 0 && <p className="text-xs text-muted-foreground italic">No previous orgs yet — click "Add Org".</p>}
                {form.previous_orgs.map((o, i) => (
                  <div key={i} className="rounded-md border p-2.5 grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Label className="text-[10px] text-muted-foreground">Company</Label>
                      <Input className="h-8 text-sm" value={o.name} onChange={(e) => {
                        const next = [...form.previous_orgs]; next[i] = { ...next[i], name: e.target.value }; setForm({ ...form, previous_orgs: next });
                      }} placeholder="Company name" data-testid={`prov-org-name-${i}`} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] text-muted-foreground">Role</Label>
                      <Input className="h-8 text-sm" value={o.role} onChange={(e) => {
                        const next = [...form.previous_orgs]; next[i] = { ...next[i], role: e.target.value }; setForm({ ...form, previous_orgs: next });
                      }} placeholder="e.g. Senior Engineer" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] text-muted-foreground">From</Label>
                      <Input className="h-8 text-sm" type="date" value={o.start_date || ''} onChange={(e) => {
                        const next = [...form.previous_orgs]; next[i] = { ...next[i], start_date: e.target.value }; setForm({ ...form, previous_orgs: next });
                      }} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] text-muted-foreground">To</Label>
                      <div className="flex gap-1">
                        <Input className="h-8 text-sm flex-1" type="date" value={o.end_date || ''} onChange={(e) => {
                          const next = [...form.previous_orgs]; next[i] = { ...next[i], end_date: e.target.value }; setForm({ ...form, previous_orgs: next });
                        }} />
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => {
                          setForm({ ...form, previous_orgs: form.previous_orgs.filter((_, j) => j !== i) });
                        }}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Breaks */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Career Breaks</Label>
                <Button size="sm" variant="outline" className="gap-1 h-7 text-xs"
                  onClick={() => setForm({ ...form, breaks: [...form.breaks, { from: '', to: '', reason: '' }] })}
                  data-testid="prov-add-break"
                >
                  <Plus className="w-3 h-3" /> Add Break
                </Button>
              </div>
              <div className="space-y-2">
                {form.breaks.length === 0 && <p className="text-xs text-muted-foreground italic">No breaks recorded.</p>}
                {form.breaks.map((b, i) => (
                  <div key={i} className="rounded-md border p-2.5 grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-3">
                      <Label className="text-[10px] text-muted-foreground">From</Label>
                      <Input className="h-8 text-sm" type="date" value={b.from || ''} onChange={(e) => {
                        const next = [...form.breaks]; next[i] = { ...next[i], from: e.target.value }; setForm({ ...form, breaks: next });
                      }} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px] text-muted-foreground">To</Label>
                      <Input className="h-8 text-sm" type="date" value={b.to || ''} onChange={(e) => {
                        const next = [...form.breaks]; next[i] = { ...next[i], to: e.target.value }; setForm({ ...form, breaks: next });
                      }} />
                    </div>
                    <div className="col-span-5">
                      <Label className="text-[10px] text-muted-foreground">Reason (optional)</Label>
                      <Input className="h-8 text-sm" value={b.reason || ''} onChange={(e) => {
                        const next = [...form.breaks]; next[i] = { ...next[i], reason: e.target.value }; setForm({ ...form, breaks: next });
                      }} placeholder="Higher studies, family, etc." />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => {
                        setForm({ ...form, breaks: form.breaks.filter((_, j) => j !== i) });
                      }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setIsEditing(false)} className="gap-1.5">
                <X className="w-3.5 h-3.5" /> Cancel
              </Button>
              <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="prov-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </Button>
            </div>
          </div>
        )}

        {/* Zoom-in graphical detail */}
        <Dialog open={!!zoomItem} onOpenChange={(o) => { if (!o) setZoomItem(null); }}>
          <DialogContent className="sm:max-w-md">
            {zoomItem && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${zoomItem.kind === 'current' ? 'bg-emerald-500' : zoomItem.kind === 'break' ? 'bg-amber-500' : 'bg-primary'}`} />
                    {zoomItem.name}
                  </DialogTitle>
                  <DialogDescription>{zoomItem.kind === 'current' ? 'Current organisation' : zoomItem.kind === 'break' ? 'Career break' : 'Previous organisation'}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg bg-muted/50 p-3">
                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Start</p>
                      <p className="text-sm font-semibold mt-0.5">{zoomItem.start_date ? new Date(zoomItem.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</p>
                    </div>
                    <div className="rounded-lg bg-muted/50 p-3">
                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">End</p>
                      <p className="text-sm font-semibold mt-0.5">{zoomItem.end_date ? new Date(zoomItem.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Present'}</p>
                    </div>
                  </div>
                  {zoomItem.role && (
                    <div className="rounded-lg bg-muted/50 p-3">
                      <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Role</p>
                      <p className="text-sm font-semibold mt-0.5">{zoomItem.role}</p>
                    </div>
                  )}
                  {zoomItem.start_date && (() => {
                    const s = new Date(zoomItem.start_date);
                    const e = zoomItem.end_date ? new Date(zoomItem.end_date) : new Date();
                    const months = Math.max(0, Math.round((e - s) / (1000 * 60 * 60 * 24 * 30.4375)));
                    const yrs = Math.floor(months / 12);
                    const rem = months % 12;
                    return (
                      <div className="rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 border p-3 text-center">
                        <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Duration</p>
                        <p className="text-xl font-bold mt-1 text-primary">{yrs}y {rem}m</p>
                      </div>
                    );
                  })()}
                </div>
                <div className="flex justify-end">
                  <Button onClick={() => setZoomItem(null)}>Close</Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ProvenanceTab;
